import {
    ClockCircleOutlined,
    SaveOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    ConfigProvider,
    InputNumber,
    Layout,
    message,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tabs,
    Tag,
    theme as antdTheme,
    Typography,
} from 'antd';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apis from '../../apis';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import {
    createDefaultVideoPenaltyConfig,
    fetchVideoPenaltyConfig,
    saveVideoPenaltyConfig,
} from '../../utils/videoPenaltyConfig';
import {
    getVideoPenaltyRuleKey,
    getVideoPenaltyRuleLabels,
    validateVideoPenaltyConfig,
    VIDEO_PENALTY_CATEGORIES,
} from '../../utils/videoPenaltyRules';

const { Content } = Layout;
const { Text, Title } = Typography;

const VideoPenaltySettings = () => {
    const { t } = useTranslation();
    const { currentEventId } = useAuth();
    const [selectedEventId, setSelectedEventId] = useState(currentEventId || 'APCS2026');
    const [eventOptions, setEventOptions] = useState([]);
    const [config, setConfig] = useState(null);
    const [hasExistingConfig, setHasExistingConfig] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [legacyPenaltyScore, setLegacyPenaltyScore] = useState(5);

    useEffect(() => {
        setSelectedEventId(currentEventId || 'APCS2026');
    }, [currentEventId]);

    useEffect(() => {
        const loadPageMetadata = async () => {
            try {
                const [eventSnapshot, settingsResponse] = await Promise.all([
                    getDocs(query(collection(db, 'events'), limit(50))),
                    apis.systemSettings.getGlobalSettings(),
                ]);
                const ids = eventSnapshot.docs.map((eventDoc) => eventDoc.id);
                const currentId = currentEventId || 'APCS2026';
                setEventOptions([...new Set([currentId, ...ids])].map((eventId) => ({
                    label: eventId,
                    value: eventId,
                })));
                const legacyScore = Number(settingsResponse?.data?.videoPenaltyScore);
                if (Number.isFinite(legacyScore)) setLegacyPenaltyScore(legacyScore);
            } catch (error) {
                console.error('Failed to load video penalty metadata:', error);
                message.error(t('videoPenalty.metadataLoadError'));
            }
        };
        loadPageMetadata();
    }, [currentEventId, t]);

    const loadConfig = useCallback(async () => {
        setLoading(true);
        try {
            const storedConfig = await fetchVideoPenaltyConfig(selectedEventId);
            setHasExistingConfig(Boolean(storedConfig));
            setConfig(storedConfig || createDefaultVideoPenaltyConfig(legacyPenaltyScore));
        } catch (error) {
            console.error('Failed to load video penalty configuration:', error);
            message.error(t('videoPenalty.configLoadError'));
            setConfig(createDefaultVideoPenaltyConfig(legacyPenaltyScore));
            setHasExistingConfig(false);
        } finally {
            setLoading(false);
        }
    }, [selectedEventId, legacyPenaltyScore, t]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const validation = useMemo(
        () => validateVideoPenaltyConfig(config),
        [config]
    );

    const updateRuleMaximum = (ruleKey, maximumMinutes) => {
        setConfig((previous) => ({
            ...previous,
            rules: previous.rules.map((rule) => (
                getVideoPenaltyRuleKey(rule) === ruleKey
                    ? { ...rule, maximumMinutes }
                    : rule
            )),
        }));
    };

    const handleSave = async () => {
        if (!validation.isValid) {
            message.error(t('videoPenalty.invalidConfig'));
            return;
        }

        setSaving(true);
        try {
            const savedConfig = await saveVideoPenaltyConfig(
                selectedEventId,
                config
            );
            setConfig(savedConfig);
            setHasExistingConfig(true);
            message.success(t('videoPenalty.saveSuccess', {
                eventId: selectedEventId,
                revision: savedConfig.revision,
            }));
        } catch (error) {
            console.error('Failed to save video penalty configuration:', error);
            message.error(t('videoPenalty.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: t('videoPenalty.entryType'),
            key: 'entryType',
            render: (_, rule) => {
                const labels = getVideoPenaltyRuleLabels(rule);
                const instrumentCategoryLabel = rule.instrumentCategory
                    ? t(`register.instrumentList.${rule.instrumentCategory}`, {
                        defaultValue: labels.instrumentCategoryLabel,
                    })
                    : '';
                const ageCategoryLabel = t(`register.ageCategories.${rule.ageCategory}`, {
                    defaultValue: labels.ageCategoryLabel,
                });
                return (
                    <Space direction="vertical" size={0}>
                        <Text strong>{instrumentCategoryLabel || ageCategoryLabel}</Text>
                        {instrumentCategoryLabel && (
                            <Text type="secondary" style={{ fontSize: 12 }}>{ageCategoryLabel}</Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: t('videoPenalty.performance'),
            dataIndex: 'performanceCategory',
            width: 120,
            render: (value) => (
                <Tag color={value === 'Solo' ? 'gold' : 'purple'}>
                    {t(`register.PerformanceCategory.${value}`, { defaultValue: value })}
                </Tag>
            ),
        },
        {
            title: t('videoPenalty.maximumMinutes'),
            dataIndex: 'maximumMinutes',
            width: 180,
            render: (value, rule) => (
                <InputNumber
                    min={1}
                    precision={0}
                    value={value}
                    onChange={(nextValue) => updateRuleMaximum(getVideoPenaltyRuleKey(rule), nextValue)}
                    style={{ width: '100%' }}
                    addonAfter={t('videoPenalty.minutesShort')}
                />
            ),
        },
    ];

    const categoryTabs = VIDEO_PENALTY_CATEGORIES.map((category) => ({
        key: category,
        label: t(`register.competitionList.${category}`, { defaultValue: category }),
        children: (
            <Table
                rowKey={getVideoPenaltyRuleKey}
                columns={columns}
                dataSource={(config?.rules || []).filter((rule) => rule.competitionCategory === category)}
                pagination={false}
                size="small"
            />
        ),
    }));

    return (
        <ConfigProvider
            theme={{
                algorithm: antdTheme.darkAlgorithm,
                token: {
                    colorBgBase: '#121212',
                    colorBgContainer: '#1E1E1E',
                    colorBorder: '#4d4128',
                    colorPrimary: '#EBBC64',
                    colorText: '#e5cc92',
                    colorTextSecondary: '#b9a77f',
                    colorTextLightSolid: '#121212',
                },
            }}
        >
        <Content style={{ padding: '24px 50px', minHeight: 280, background: '#121212' }}>
            <Row justify="space-between" align="top" gutter={[16, 16]}>
                <Col>
                    <Title level={2} style={{ marginBottom: 4, color: '#EBBC64' }}>
                        <ClockCircleOutlined style={{ marginRight: 10 }} />
                        {t('videoPenalty.settingsTitle')}
                    </Title>
                    <Text style={{ color: '#e5cc92' }}>
                        {t('videoPenalty.settingsDescription')}
                    </Text>
                </Col>
                <Col>
                    <Space>
                        <Select
                            value={selectedEventId}
                            options={eventOptions}
                            onChange={setSelectedEventId}
                            style={{ width: 160 }}
                            showSearch
                        />
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            disabled={loading || !validation.isValid}
                            onClick={handleSave}
                        >
                            {t('videoPenalty.saveAll')}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {!hasExistingConfig && !loading && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message={t('videoPenalty.unsavedTitle', { eventId: selectedEventId })}
                    description={t('videoPenalty.unsavedDescription')}
                    style={{ marginTop: 20 }}
                />
            )}

            {!validation.isValid && !loading && (
                <Alert
                    type="error"
                    showIcon
                    message={t('videoPenalty.incomplete')}
                    description={t('videoPenalty.invalidDescription')}
                    style={{ marginTop: 20 }}
                />
            )}

            <Card style={{ marginTop: 20, background: '#1E1E1E', borderColor: '#4d4128' }}>
                {loading || !config ? (
                    <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
                ) : (
                    <>
                        <Row gutter={[16, 16]} align="bottom" style={{ marginBottom: 20 }}>
                            <Col xs={24} sm={8} md={6}>
                                <Text strong>{t('videoPenalty.sharedDeduction')}</Text>
                                <InputNumber
                                    min={1}
                                    precision={0}
                                    value={config.penaltyScore}
                                    onChange={(penaltyScore) => setConfig((previous) => ({ ...previous, penaltyScore }))}
                                    addonAfter={t('videoPenalty.points')}
                                    style={{ width: '100%', marginTop: 8 }}
                                />
                            </Col>
                            <Col>
                                <Tag color="gold">
                                    {hasExistingConfig
                                        ? t('videoPenalty.revision', { revision: config.revision })
                                        : t('videoPenalty.notSaved')}
                                </Tag>
                            </Col>
                        </Row>
                        <Tabs items={categoryTabs} destroyInactiveTabPane />
                    </>
                )}
            </Card>
        </Content>
        </ConfigProvider>
    );
};

export default VideoPenaltySettings;
